/**
 * API Client (Axios)
 * CCH Care - Version 1.0
 * 
 * Configured with:
 * - Base URL from config
 * - JWT token injection via request interceptor
 * - Tenant/membership headers
 * - 401 response handling (auto-logout)
 * - Response error normalization
 */

import axios from 'axios';
import API_BASE_URL from '../config/api';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30s timeout (tablet networks can be slow)
});

/**
 * Request interceptor — inject auth headers
 */
client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('care_token');
    const membershipId = localStorage.getItem('care_membership_id');
    const tenantId = localStorage.getItem('care_tenant_id');
    const facilityId = localStorage.getItem('care_facility_id');

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (membershipId) {
      config.headers['X-Membership-ID'] = membershipId;
    }
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }
    if (facilityId) {
      config.headers['X-Facility-ID'] = facilityId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor — handle 401 (session expired)
 */
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid — clear auth and redirect to login
      localStorage.removeItem('care_token');
      localStorage.removeItem('care_membership_id');
      localStorage.removeItem('care_tenant_id');
      localStorage.removeItem('care_facility_id');
      
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?expired=1';
      }
    }
    
    // Normalize error message
    const message = error.response?.data?.error 
      || error.response?.data?.message 
      || error.message 
      || 'An unexpected error occurred';
    
    return Promise.reject({ ...error, message });
  }
);

export default client;
